/* eslint-disable react-hooks/rules-of-hooks */
import { useTranslation } from 'react-i18next';
import { formatter, type Task } from '@repo/shared';
import type { ColumnDef } from '@tanstack/react-table';

import type { ClustersQueryResult } from '@/api/generated';
import { ColumnHeader } from '@/components/blocks/data-table/column-header';
import SelectableHeader from '@/components/blocks/data-table/selectable-header';
import { SelectableRow } from '@/components/blocks/data-table/selectable-row';
import StatusBadge from '@/routes/(app)/(clusters)/components/status-badge';
import TaskBadge from '@/routes/(app)/(datasets)/components/task-badge';

type Cluster = ClustersQueryResult['data'][number];

export const columns: Array<ColumnDef<Cluster>> = [
  {
    accessorKey: 'id',
    header: ({ table }) => <SelectableHeader table={table} />,
    cell: ({ row }) => <SelectableRow row={row} />,
  },
  {
    accessorKey: 'dataset',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('clusters.table.dataset')}
        />
      );
    },
    cell: ({ row }) => <span>{row.original.dataset.name}</span>,
  },
  {
    accessorKey: 'task',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('clusters.table.task')}
        />
      );
    },
    cell: ({ row }) => <TaskBadge task={row.original.dataset.task as Task} />,
  },
  {
    accessorKey: 'model',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('clusters.table.model')}
        />
      );
    },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('clusters.table.createdAt')}
          enableSorting
        />
      );
    },
    cell: ({ row }) =>
      formatter.date.dateTime(new Date(row.original.createdAt as string)),
  },
  {
    accessorKey: 'createdBy',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('clusters.table.createdBy')}
        />
      );
    },
    cell: ({ row }) => <span>{row.original.createdBy.email}</span>,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('clusters.table.status')}
        />
      );
    },
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
];
